import * as Popover from '@radix-ui/react-popover'
import { Info, Lock, Trash2 } from 'lucide-react'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { FieldNote } from '@/components/ui/FieldNote'
import { Tooltip } from '@/components/ui/Tooltip'
import { toDecimal, toNumber } from '@/lib/decimal'
import { formatEuros } from '@/lib/format'
import { formatHmm, parseFlexibleHours, parseMoneyStrict, travelMessage } from '@/lib/hours'
import type { CellValues } from '@/lib/time-queries'
import { descriptionCopy, hoursMessage, MAX_DAY_HOURS, readDescription } from '@/lib/timesheet-copy'
import { cellNeedsConfirm, deleteCopy } from '@/lib/timesheet-delete'
import type { WeekCell, WeekRow } from '@/lib/types'
import { cn } from '@/lib/utils'

interface CellPopoverProps {
  row: WeekRow
  cell: WeekCell
  editable: boolean
  cellId: string
  otherHoursOnDay: number
  onSave: (values: CellValues) => void
  /**
   * Waarom deze cel niet te bewerken is, als dat niet al aan de cel zelf ligt.
   * Een gefactureerde cel zegt het zelf; een afgesloten week weet alleen het grid.
   */
  readOnlyNote?: string | null
  /** Vervangt de compacte celknop, bijvoorbeeld door een hele kaart op mobiel. */
  trigger?: ReactNode
}


export function CellPopover({ row, cell, editable, cellId, otherHoursOnDay, onSave, readOnlyNote, trigger: customTrigger }: CellPopoverProps) {
  const [open, setOpen] = useState(false)
  const filled = toNumber(cell.hours) > 0
  const travelAmount = toNumber(cell.travelAmount)
  const travelLabel = travelAmount > 0 ? formatEuros(travelAmount) : null

  // Niet kunnen wijzigen is iets anders dan niet mogen weten. Een gefactureerde cel
  // of een afgesloten week blijft daarom klikbaar, alleen opent hij een leesvenster
  // in plaats van een formulier. De dagweergave en de mobiele kaart zetten
  // omschrijving en reiskosten al in beeld en houden daarom hun eigen trigger.
  if (cell.locked || !editable) {
    if (customTrigger) return <>{customTrigger}</>

    const note = cell.locked
      ? cell.invoiceNumber
        ? `Gefactureerd op ${cell.invoiceNumber}`
        : 'Gefactureerd'
      : (readOnlyNote ?? null)
    const hasDetail = filled || travelAmount > 0 || cell.description.trim() !== ''

    const readTrigger = (
      <button
        type="button"
        id={cellId}
        data-cell={cellId}
        disabled={!hasDetail}
        aria-label={[
          filled ? formatHmm(toNumber(cell.hours)) : 'Geen uren',
          travelLabel ? `reiskosten ${travelLabel}` : null,
          hasDetail ? 'bekijk de regel' : null,
        ]
          .filter(Boolean)
          .join(', ')}
        className={cn(
          'mx-auto flex min-h-9 w-full min-w-12 flex-col items-center justify-center gap-0.5 rounded-md py-1 text-sm transition-colors',
          cell.locked ? 'bg-fill-2 text-ink-faint' : filled ? 'text-ink' : 'text-ink-faint/40',
          hasDetail && 'hover:bg-accent-600/[0.07] focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-500/30',
          open && 'bg-accent-600/[0.07] ring-2 ring-accent-500/30',
        )}
      >
        <span className={cn('flex items-center gap-1 leading-none', filled && 'font-medium')}>
          {cell.locked ? <Lock className="h-3 w-3" aria-hidden="true" /> : null}
          <span className="num">{filled ? formatHmm(toNumber(cell.hours)) : '·'}</span>
        </span>
        {travelLabel ? <span className="money text-[10px] leading-none text-ink-faint">{travelLabel}</span> : null}
      </button>
    )

    if (!hasDetail) return readTrigger

    return (
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>{readTrigger}</Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            sideOffset={6}
            align="center"
            className="z-40 w-64 rounded-card border border-hairline bg-surface p-3 shadow-pop animate-fade-in focus:outline-hidden"
          >
            <CellDetails row={row} cell={cell} note={note} />
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    )
  }

  const trigger = customTrigger ?? (
    <button
      type="button"
      id={cellId}
      data-cell={cellId}
      aria-label={
        [filled ? formatHmm(toNumber(cell.hours)) : 'Uren invoeren', travelLabel ? `reiskosten ${travelLabel}` : null]
          .filter(Boolean)
          .join(', ')
      }
      className={cn(
        'mx-auto flex min-h-9 w-full min-w-12 flex-col items-center justify-center gap-0.5 rounded-md py-1 text-sm transition-colors',
        'hover:bg-accent-600/[0.07] focus:bg-accent-600/[0.07] focus:outline-hidden focus:ring-2 focus:ring-accent-500/30',
        !filled && 'border border-hairline bg-surface hover:border-accent-500/40',
        filled && 'text-ink',
        open && 'bg-accent-600/[0.07] ring-2 ring-accent-500/30',
      )}
    >
      <span className={cn('num leading-none', filled && 'font-medium')}>
        {filled ? formatHmm(toNumber(cell.hours)) : null}
      </span>
      {travelLabel ? <span className="money text-[10px] leading-none text-ink-faint">{travelLabel}</span> : null}
    </button>
  )

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>{trigger}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          sideOffset={6}
          align="center"
          className="z-40 w-72 rounded-card border border-hairline bg-surface p-3 shadow-pop animate-fade-in focus:outline-hidden"
          onOpenAutoFocus={(event) => {
            event.preventDefault()
            const input = document.getElementById(`${cellId}-hours`) as HTMLInputElement | null
            input?.focus()
            input?.select()
          }}
        >
          <CellForm
            row={row}
            cell={cell}
            cellId={cellId}
            otherHoursOnDay={otherHoursOnDay}
            onCancel={() => setOpen(false)}
            onSave={(values) => {
              setOpen(false)
              onSave(values)
            }}
            onDelete={
              cell.entryId
                ? () => {
                    setOpen(false)
                    onSave({ hours: '0.00', description: '', travelAmount: null })
                  }
                : undefined
            }
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}

/**
 * Dezelfde regel als in het formulier, maar om te lezen. Wat je niet meer kunt
 * wijzigen wil je juist wel kunnen nakijken: welke omschrijving ging er mee, en
 * hingen er reiskosten aan.
 */
function CellDetails({ row, cell, note }: { row: WeekRow; cell: WeekCell; note: string | null }) {
  const hours = toNumber(cell.hours)
  const travelAmount = toNumber(cell.travelAmount)
  const description = readDescription(cell.description, hours)

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-medium text-ink">{row.clientName}</span>
        <span className="truncate text-xs text-ink-faint">
          {row.projectName}
          {row.categoryName ? ` · ${row.categoryName}` : ''}
        </span>
      </div>

      <div className="divide-y divide-hairline border-y border-hairline">
        <div className="flex items-baseline justify-between py-1.5">
          <span className="label-caps">Uren</span>
          <span className="num text-sm font-medium text-ink">{formatHmm(hours)}</span>
        </div>
        {travelAmount > 0 ? (
          <div className="flex items-baseline justify-between py-1.5">
            <span className="label-caps">Reiskosten</span>
            <span className="money text-sm text-ink">{formatEuros(travelAmount)}</span>
          </div>
        ) : null}
      </div>

      <div>
        <span className="label-caps">Omschrijving</span>
        <p className={cn('mt-1 whitespace-pre-wrap text-sm', description.missing ? 'text-ink-faint/70' : 'text-ink-soft')}>
          {description.text || descriptionCopy.missing}
        </p>
      </div>

      {note ? (
        <p className="flex items-center gap-1.5 text-xs text-ink-faint">
          <Lock className="h-3 w-3 shrink-0" aria-hidden="true" />
          {note}
        </p>
      ) : null}
    </div>
  )
}

interface CellFormProps {
  row: WeekRow
  cell: WeekCell
  cellId: string
  otherHoursOnDay: number
  onSave: (values: CellValues) => void
  onCancel: () => void
  onDelete?: () => void
}

function CellForm({ row, cell, cellId, otherHoursOnDay, onSave, onCancel, onDelete }: CellFormProps) {
  const [hours, setHours] = useState(toNumber(cell.hours) > 0 ? formatHmm(toNumber(cell.hours)) : '')
  const [description, setDescription] = useState(cell.description)
  const [travel, setTravel] = useState(cell.travelAmount ? String(toNumber(cell.travelAmount)).replace('.', ',') : '')
  const [saveAttempted, setSaveAttempted] = useState(false)
  const [armed, setArmed] = useState(false)
  const armedRef = useRef<HTMLButtonElement>(null)

  // De prullenbak verdwijnt uit de DOM bij het wapenen, dus zonder dit valt de focus
  // terug naar body en staat een toetsenbordgebruiker buiten het paneel.
  useEffect(() => {
    if (armed) armedRef.current?.focus()
  }, [armed])

  const parsedHours = hours.trim() === '' ? 0 : parseFlexibleHours(hours)
  const invalidHours = parsedHours === null
  const enteredHours = parsedHours ?? 0
  const prospectiveDayTotal = otherHoursOnDay + enteredHours
  const overDayLimit = prospectiveDayTotal > MAX_DAY_HOURS
  const hoursError = hoursMessage(invalidHours, prospectiveDayTotal)
  const isNewEntry = cell.entryId === null
  const hadDescription = cell.description.trim() !== ''
  const descriptionMissing = enteredHours > 0 && description.trim() === '' && (isNewEntry || hadDescription)
  const parsedTravel = travel.trim() === '' ? 0 : parseMoneyStrict(travel)
  const travelError = travelMessage(parsedTravel)
  // Reiskosten horen bij een regel met uren. Op een bestaande regel betekent uren
  // weghalen "verwijder deze regel", en die weg blijft open; op een lege cel zou het
  // bedrag zonder uren nergens landen.
  const travelNeedsHours = isNewEntry && enteredHours <= 0 && (parsedTravel ?? 0) > 0
  // Een knop is alleen uitgeschakeld als op het scherm al staat waarom. Bij de andere
  // fouten staat dat er; bij de omschrijving doet de klik het werk.
  const canSubmit = !invalidHours && !overDayLimit && travelError === null && !travelNeedsHours
  // Alleen als de omschrijving de laatste blokkade is: anders wijst "niet opgeslagen"
  // de verkeerde oorzaak aan terwijl de echte melding er al staat.
  const blocked = descriptionMissing && canSubmit && saveAttempted

  if (saveAttempted && !descriptionMissing) setSaveAttempted(false)

  function submit() {
    if (!canSubmit) return
    if (descriptionMissing) {
      setSaveAttempted(true)
      document.getElementById(`${cellId}-desc`)?.focus()
      return
    }
    onSave({
      hours: toDecimal(enteredHours),
      description: description.trim(),
      travelAmount: travel.trim() === '' ? null : toDecimal(parsedTravel ?? 0),
    })
  }

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-medium text-ink">{row.clientName}</span>
        <span className="text-xs text-ink-faint">
          {row.projectName}
          {row.categoryName ? ` · ${row.categoryName}` : ''}
        </span>
      </div>

      <div>
        {/* Het dagtotaal staat op de labelregel die er toch al is, en verklaart daarmee de
            rode rand zodra je boven de grens komt. Een melding onder het veld zou het
            paneel tijdens het typen van hoogte laten veranderen. */}
        <div className="mb-1 flex items-baseline justify-between gap-2">
          <label htmlFor={`${cellId}-hours`} className="block text-xs font-medium text-ink-soft">
            Uren
          </label>
          {!invalidHours && enteredHours > 0 ? (
            <span className={cn('text-xs', overDayLimit ? 'font-medium text-status-overdue' : 'text-ink-faint')}>
              Dagtotaal wordt {formatHmm(prospectiveDayTotal)}
            </span>
          ) : null}
        </div>
        <Tooltip content={hoursError}>
          <input
            id={`${cellId}-hours`}
            value={hours}
            onChange={(event) => setHours(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                submit()
              }
            }}
            placeholder="0:00"
            aria-invalid={hoursError !== null}
            aria-describedby={hoursError ? `${cellId}-hours-note` : undefined}
            className={cn(
              'field num text-right transition-colors duration-150',
              hoursError !== null && 'border-status-overdue focus:border-status-overdue',
            )}
          />
        </Tooltip>
        <span id={`${cellId}-hours-note`} className="sr-only">
          {hoursError ?? ''}
        </span>
      </div>

      <div>
        <label htmlFor={`${cellId}-desc`} className="mb-1 block text-xs font-medium text-ink-soft">
          Omschrijving
        </label>
        <div className="relative">
          <input
            id={`${cellId}-desc`}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                submit()
              }
            }}
            placeholder="Waar werkte je aan?"
            aria-required={descriptionMissing}
            aria-invalid={blocked}
            aria-describedby={descriptionMissing ? `${cellId}-desc-req` : undefined}
            className={cn(
              'field transition-colors duration-150',
              descriptionMissing && (blocked ? 'pr-28' : 'pr-16'),
              blocked && 'border-status-overdue focus:border-status-overdue',
            )}
          />
          {descriptionMissing ? (
            <FieldNote tone={blocked ? 'attention' : 'muted'}>
              {blocked ? descriptionCopy.blocked : descriptionCopy.required}
            </FieldNote>
          ) : null}
          <span id={`${cellId}-desc-req`} className="sr-only">
            {descriptionCopy.requiredHint}
          </span>
          {/* Leeg gemonteerd, anders kondigt een schermlezer de omslag niet aan. */}
          <span role="status" aria-live="polite" className="sr-only">
            {blocked ? descriptionCopy.blockedMessage : ''}
          </span>
        </div>
      </div>

      <div>
        <label htmlFor={`${cellId}-travel`} className="mb-1 block text-xs font-medium text-ink-soft">
          Reiskosten €
        </label>
        <Tooltip content={travelError}>
          <input
            id={`${cellId}-travel`}
            inputMode="decimal"
            value={travel}
            onChange={(event) => setTravel(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                submit()
              }
            }}
            placeholder="0,00"
            aria-invalid={travelError !== null}
            aria-describedby={travelError ? `${cellId}-travel-note` : undefined}
            className={cn(
              'field num text-right transition-colors duration-150',
              travelError !== null && 'border-status-overdue focus:border-status-overdue',
            )}
          />
        </Tooltip>
        <span id={`${cellId}-travel-note`} className="sr-only">
          {travelError ?? ''}
        </span>
        {travelNeedsHours ? <p className="mt-1 text-xs text-ink-faint">Vul eerst uren in op deze regel.</p> : null}
        {row.travelAgreement ? (
          <p className="mt-1 flex items-start gap-1 text-xs text-ink-faint">
            <Info className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
            <span>{row.travelAgreement}</span>
          </p>
        ) : null}
        {/*
         * Het bedrag van de vorige keer vult alleen op een klik, nooit vanzelf: het veld
         * staat altijd open, dus voorvullen zou bij het opslaan van enkel uren stilzwijgend
         * een reiskost aanmaken. Leeg laten verwijdert juist een bestaande reiskost.
         */}
        {row.lastTravelAmount !== null ? (
          <button
            type="button"
            onClick={() => setTravel(String(toNumber(row.lastTravelAmount)).replace('.', ','))}
            className="mt-1 text-xs text-ink-faint underline decoration-hairline underline-offset-2 hover:text-accent-700"
          >
            Vorige keer bij {row.clientName}: {formatEuros(toNumber(row.lastTravelAmount))}
          </button>
        ) : null}
      </div>

      {/* De bevestiging blijft in dit paneel: een dialoog erbovenop zou de popover
          sluiten of drie lagen chroom stapelen voor het wissen van één dag. Buiten
          klikken ontwapent vanzelf, dus het veilige gedrag is de uitgangspositie. */}
      {armed ? (
        <div className="flex items-center gap-2 pt-1">
          <button
            type="button"
            ref={armedRef}
            onClick={onDelete}
            className="btn-primary h-8 flex-1 bg-status-overdue hover:bg-status-overdue/90"
          >
            <Trash2 className="h-4 w-4" />
            Verwijder {formatHmm(toNumber(cell.hours))}
          </button>
          <button type="button" onClick={() => setArmed(false)} className="btn-ghost h-8 px-2 text-sm">
            {deleteCopy.cancel}
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2 pt-1">
          <button type="button" onClick={submit} disabled={!canSubmit} className="btn-primary h-8 flex-1 disabled:cursor-not-allowed disabled:opacity-50">
            Opslaan
          </button>
          {onDelete ? (
            <button
              type="button"
              onClick={() => (cellNeedsConfirm(cell) ? setArmed(true) : onDelete())}
              className="btn-ghost h-8 w-8 p-0 text-status-overdue"
              aria-label="Uren van deze dag verwijderen"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          ) : null}
          <button type="button" onClick={onCancel} className="btn-ghost h-8 px-2 text-sm">
            Esc
          </button>
        </div>
      )}
      <span role="status" aria-live="polite" className="sr-only">
        {armed ? `Bevestig verwijderen: ${formatHmm(toNumber(cell.hours))} op ${row.clientName} ${row.projectName}.` : ''}
      </span>
    </div>
  )
}
